import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable, catchError, map, tap} from 'rxjs';
import {HttpService} from '../http.service';
import {HttpErrorHandlerService} from '../http-error-handler.service';
import {environment} from '../../../environments/environment';
import { AppStorageService } from '../app-storage.service';
import { StorageKeys } from '../storage-keys';

@Injectable({
  providedIn: 'root',
})
export class NotificationService {
  private apiUrl = `${environment.api.notifications}`;

  // BehaviorSubject para manejar notificationCount de forma reactiva
  private notificationCountSubject = new BehaviorSubject<number>(0);
  notificationCount$ = this.notificationCountSubject.asObservable();

  constructor(
    private httpService: HttpService,
    private errorHandler: HttpErrorHandlerService,
    private storage: AppStorageService
  ) {
    this.loadNotificationCountFromStorage();
  }

  /**
   * Obtiene las notificaciones y actualiza el contador.
   * @param page Número de página
   * @param sortField Campo por el que ordenar
   * @param sortOrder Dirección de ordenamiento (1: ascendente, -1: descendente)
   */
  getNotifications(page: number = 1, sortField: string = 'created_at', sortOrder: number = -1): Observable<any> {
    const orderDir = sortOrder === 1 ? 'asc' : 'desc';
    return this.httpService.get(`${this.apiUrl}?page=${page}&sort=${sortField}&order=${orderDir}`).pipe(
      map((response: any) => {
        return {
          notifications: response.data,
          pagination: {
            currentPage: response.current_page,
            totalPages: response.last_page,
            totalItems: response.total,
            perPage: response.per_page,
            nextPageUrl: response.next_page_url,
            prevPageUrl: response.prev_page_url,
          },
        };
      }),
      catchError((error) => this.errorHandler.handleError(error))
    );
  }

  /**
   * Obtiene la cantidad de notificaciones sin leer.
   */
  getUnreadNotificationCount(): Observable<number> {
    return this.httpService.get(`${this.apiUrl}/unread-count`).pipe(
      map((response: any) => {
        const unreadCount = response.unread_count || 0;

        // Actualiza el BehaviorSubject
        this.notificationCountSubject.next(unreadCount);

        // Almacena la cantidad en el storage
        this.storage.set(StorageKeys.NOTIFICATION_COUNT, unreadCount.toString());

        return unreadCount;
      }),
      catchError((error) => this.errorHandler.handleError(error))
    );
  }

  /**
   * Carga la cantidad de notificaciones desde localStorage al iniciar.
   */
  loadNotificationCountFromStorage(): void {
    const storedCount = this.storage.get(StorageKeys.NOTIFICATION_COUNT);
    const count = storedCount ? parseInt(storedCount, 10) : 0;

    // Actualiza el BehaviorSubject con el valor almacenado
    this.notificationCountSubject.next(count);
  }

  /**
   * Marca una notificación como leída y actualiza el contador.
   */
  markAsRead(id: number): Observable<any> {
    return this.httpService.post(`${this.apiUrl}/${id}/mark-as-read`, {}).pipe(
      tap(() => {
        // Reduce el contador de notificaciones sin leer
        const currentCount = this.notificationCountSubject.value;
        const updatedCount = Math.max(0, currentCount - 1);

        // Actualiza el BehaviorSubject y el storage
        this.notificationCountSubject.next(updatedCount);
        this.storage.set(StorageKeys.NOTIFICATION_COUNT, updatedCount.toString());
      }),
      catchError((error) => this.errorHandler.handleError(error))
    );
  }

  /**
   * Elimina una notificación y actualiza el contador.
   */
  deleteNotification(id: number, viewed: boolean): Observable<any> {
    return this.httpService.delete<any>(`${this.apiUrl}/${id}`).pipe(
      tap((response) => {
        // Si la notificación eliminada no estaba leída (viewed es false), reduce el contador
        if (!viewed) {
          const currentCount = this.notificationCountSubject.value;
          const updatedCount = Math.max(0, currentCount - 1);

          // Actualiza el BehaviorSubject y el storage
          this.notificationCountSubject.next(updatedCount);
          this.storage.set(StorageKeys.NOTIFICATION_COUNT, updatedCount.toString());
        }

        return response;
      }),
      catchError((error) => this.errorHandler.handleError(error))
    );
  }
}
